import { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useToast } from "../../../contexts/ToastContext";
import { useUploadImageMutation } from "../../../store/api/imageUploadApi";
import type { Lease } from "../../../store/api/leasesApi";

interface UploadSignedAgreementModalProps {
	lease: Lease | null;
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
}

const UploadSignedAgreementModal: React.FC<
	UploadSignedAgreementModalProps
> = ({ lease, isOpen, onClose, onSuccess }) => {
	const [uploadImage, { isLoading: isUploadingImage }] =
		useUploadImageMutation();
	const [documentFile, setDocumentFile] = useState<File | null>(null);
	const [documentPreview, setDocumentPreview] = useState<string | null>(null);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const { success, error: showError } = useToast();

	const isLoading = isUploadingImage;

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) {
			setErrors({ file: "Please select a file" });
			return;
		}

		// Validate file type - PDF and images only (JPG, JPEG, PNG)
		const allowedTypes = [
			"application/pdf",
			"image/jpeg",
			"image/jpg",
			"image/png",
		];
		const allowedExtensions = [".pdf", ".jpg", ".jpeg", ".png"];

		const isValidType = allowedTypes.includes(file.type?.toLowerCase());
		const isValidExtension = allowedExtensions.some((ext) =>
			file.name?.toLowerCase().endsWith(ext),
		);

		if (!isValidType && !isValidExtension) {
			setErrors({
				file: "Please select a PDF or image file (JPG, JPEG, PNG only)",
			});
			return;
		}

		// Validate file size (10MB max)
		const maxSize = 10 * 1024 * 1024;
		if (file.size > maxSize) {
			setErrors({ file: "File size must be less than 10MB" });
			return;
		}

		// Validate file size (minimum 1KB)
		if (file.size < 1024) {
			setErrors({
				file: "File size is too small. Please select a valid document.",
			});
			return;
		}

		// Clear previous errors
		setErrors((prev) => ({ ...prev, file: "" }));

		// Set file and create preview for images
		setDocumentFile(file);
		if (file.type.startsWith("image/")) {
			const reader = new FileReader();
			reader.onloadend = () => {
				if (reader.result && typeof reader.result === "string") {
					setDocumentPreview(reader.result);
				}
			};
			reader.onerror = () => {
				setErrors({ file: "Failed to read file. Please try again." });
				setDocumentFile(null);
				setDocumentPreview(null);
			};
			reader.readAsDataURL(file);
		} else {
			setDocumentPreview(null);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// Validation
		if (!lease) {
			const errorMsg =
				"Lease information is missing. Please refresh and try again.";
			setErrors({ submit: errorMsg });
			showError(errorMsg);
			return;
		}

		if (!lease.id) {
			const errorMsg = "Invalid lease ID. Please refresh and try again.";
			setErrors({ submit: errorMsg });
			showError(errorMsg);
			return;
		}

		if (!documentFile) {
			setErrors({ file: "Please select a signed agreement document" });
			return;
		}

		// Clear previous errors
		setErrors({});

		try {
			// Convert file to base64
			const base64 = await new Promise<string>((resolve, reject) => {
				const reader = new FileReader();
				reader.onloadend = () => {
					if (!reader.result || typeof reader.result !== "string") {
						reject(new Error("Failed to read file"));
						return;
					}
					// Remove data:...;base64, prefix
					const parts = reader.result.split(",");
					if (parts.length < 2) {
						reject(new Error("Invalid file format"));
						return;
					}
					resolve(parts[1]);
				};
				reader.onerror = () => reject(new Error("Failed to read file"));
				reader.readAsDataURL(documentFile);
			});

			// Upload to Cloudinary
			const uploadResult = await uploadImage({
				images: [base64],
				folder: "signed_agreements",
			}).unwrap();

			if (!uploadResult.uploaded || uploadResult.uploaded.length === 0) {
				const errorMsg =
					uploadResult.failed?.[0]?.error ||
					"Failed to upload document. Please try again.";
				throw new Error(errorMsg);
			}

			const uploadedDoc = uploadResult.uploaded[0];
			if (!uploadedDoc?.url) {
				throw new Error("Document upload succeeded but no URL was returned.");
			}

			// Submit signed agreement URL to backend
			const token = localStorage.getItem("access_token");
			const response = await fetch(
				`${import.meta.env.VITE_API_BASE_URL}/leases/${lease.id}/upload-signed-agreement?signed_agreement_url=${encodeURIComponent(uploadedDoc.url)}`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.detail || "Failed to submit signed agreement");
			}

			// Reset form
			setDocumentFile(null);
			setDocumentPreview(null);
			setErrors({});
			success(
				"Signed agreement uploaded successfully. Admin has been notified.",
			);
			onSuccess();
		} catch (error: unknown) {
			console.error("Upload error:", error);
			let errorMessage = "Failed to upload signed agreement. Please try again.";

			if (error && typeof error === "object") {
				if ("data" in error) {
					const errorData = error.data as { error?: string; message?: string };
					errorMessage = errorData.error || errorData.message || errorMessage;
				} else if ("message" in error && typeof error.message === "string") {
					errorMessage = error.message;
				}
			}

			setErrors({ submit: errorMessage });
			showError(errorMessage);
		}
	};

	const handleClose = () => {
		setDocumentFile(null);
		setDocumentPreview(null);
		setErrors({});
		onClose();
	};

	if (!isOpen || !lease) return null;

	return (
		<>
			{/* Backdrop */}
			<div
				className="fixed inset-0 bg-black/50 z-1300 animate-fade-in"
				onClick={handleClose}
			/>

			{/* Modal */}
			<div className="fixed inset-0 z-1400 flex items-center justify-center p-4">
				<div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-hidden animate-scale-in">
					{/* Header */}
					<div className="flex items-center justify-between p-6 border-b border-border">
						<div>
							<h2 className="text-xl font-semibold text-foreground">
								Upload Signed Agreement
							</h2>
							<p className="text-sm text-muted-foreground mt-1">
								Lease Code: {lease.lease_code || lease.id}
							</p>
						</div>
						<Button variant="ghost" size="icon" onClick={handleClose}>
							<Icon name="X" size={20} />
						</Button>
					</div>

					{/* Content */}
					<form
						onSubmit={handleSubmit}
						className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]"
					>
						<div className="space-y-6">
							{errors.submit && (
								<div className="rounded-lg bg-destructive/20 border border-destructive/30 p-3 text-sm text-destructive">
									{errors.submit}
								</div>
							)}

							{/* Document Upload */}
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Signed Agreement Document{" "}
									<span className="text-destructive">*</span>
								</label>
								<div className="space-y-4">
									<div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
										<input
											type="file"
											accept="application/pdf,image/jpeg,image/jpg,image/png,.pdf,.jpg,.jpeg,.png"
											onChange={handleFileChange}
											className="hidden"
											id="signed-agreement-upload"
											disabled={isLoading}
										/>
										<label
											htmlFor="signed-agreement-upload"
											className="cursor-pointer flex flex-col items-center space-y-2"
										>
											<Icon
												name="Upload"
												size={32}
												className="text-muted-foreground"
											/>
											<div>
												<p className="text-sm font-medium text-foreground">
													Click to upload or drag and drop
												</p>
												<p className="text-xs text-muted-foreground mt-1">
													PDF, JPG, JPEG, PNG up to 10MB
												</p>
											</div>
										</label>
									</div>

									{documentPreview && (
										<div className="relative">
											<img
												src={documentPreview}
												alt="Document preview"
												className="w-full max-h-64 object-contain rounded-lg border border-border"
											/>
											<Button
												type="button"
												variant="destructive"
												size="icon"
												className="absolute top-2 right-2"
												onClick={() => {
													setDocumentFile(null);
													setDocumentPreview(null);
												}}
												disabled={isLoading}
											>
												<Icon name="X" size={16} />
											</Button>
										</div>
									)}

									{documentFile && !documentPreview && (
										<div className="flex items-center justify-between p-4 border border-border rounded-lg">
											<div className="flex items-center space-x-3">
												<Icon name="FileText" size={24} className="text-primary" />
												<div>
													<p className="text-sm font-medium text-foreground">
														{documentFile.name}
													</p>
													<p className="text-xs text-muted-foreground">
														{(documentFile.size / 1024 / 1024).toFixed(2)} MB
													</p>
												</div>
											</div>
											<Button
												type="button"
												variant="ghost"
												size="icon-sm"
												onClick={() => {
													setDocumentFile(null);
													setDocumentPreview(null);
												}}
												disabled={isLoading}
											>
												<Icon name="X" size={16} />
											</Button>
										</div>
									)}

									{errors.file && (
										<p className="text-sm text-destructive">{errors.file}</p>
									)}
								</div>
							</div>

							{/* Instructions */}
							<div className="bg-muted/30 rounded-lg p-4">
								<h4 className="text-sm font-medium text-foreground mb-2">
									Instructions:
								</h4>
								<ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
									<li>Download the lease agreement from the documents section</li>
									<li>Review and sign the agreement (digitally or physically)</li>
									<li>Scan or photograph the signed document</li>
									<li>Upload the signed version here</li>
									<li>Admin will be notified and will review your submission</li>
								</ol>
							</div>
						</div>

						{/* Footer */}
						<div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-border">
							<Button
								type="button"
								variant="outline"
								onClick={handleClose}
								disabled={isLoading}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={isLoading || !documentFile}>
								{isLoading ? (
									<>
										<Icon
											name="Loader2"
											size={16}
											className="mr-2 animate-spin"
										/>
										Uploading...
									</>
								) : (
									<>
										<Icon name="Upload" size={16} className="mr-2" />
										Upload Signed Agreement
									</>
								)}
							</Button>
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

export default UploadSignedAgreementModal;
